import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import axios from "axios";

import config from "../../config";
import { hpcTheme, HPC_COLORS } from "./hpcTheme";
import { HPC_BRAND } from "./hpcBrand";

const A4_WIDTH_MM = 210;

const formatLabel = (value) =>
  ({
    bank_transfer: "Bank Transfer",
    card: "Card",
    cash: "Cash",
    hpc_admin: "HPC Admin",
    online: "Online",
    qr_payment: "QR Payment",
  }[String(value || "").toLowerCase()] || (value || "")
    .toString()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" "));

const displayValue = (value) => (value === null || value === undefined || value === "" ? "—" : value);

const formatRs = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return `Rs. ${amount.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** One label/value row inside the compact receipt (plain rows, not cards). */
const Row = ({ label, value }) => (
  <div className="hpu-row">
    <span className="hpu-row-label">{label}</span>
    <span className="hpu-row-value">{displayValue(value)}</span>
  </div>
);

/**
 * Public, unauthenticated HPU receipt page. Resolved solely by the opaque
 * receipt_access_token route param — never stored, never logged, never
 * used to fall back to any authenticated/admin lookup.
 *
 * Renders a compact, fixed-A4-proportioned receipt. On narrow viewports the
 * whole sheet is visually scaled down (CSS transform) rather than reflowed
 * into a long, multi-section scrolling page.
 */
const PublicHpuReceipt = () => {
  const { token } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | success | not_found | error
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const viewportRef = useRef(null);
  const sheetRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  const loadReceipt = useCallback(async () => {
    setStatus("loading");
    setReceipt(null);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/payment-receive/hpu/receipts/public/${encodeURIComponent(token)}`,
      );
      setReceipt(response.data?.receipt || null);
      setStatus("success");
    } catch (error) {
      setStatus(error.response?.status === 404 ? "not_found" : "error");
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadReceipt();
    } else {
      setStatus("not_found");
    }
  }, [token, loadReceipt]);

  // Measure the sheet at its natural (unscaled) size, then shrink it to fit the
  // viewport width instead of letting it reflow into a taller, scrollier layout.
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    const viewport = viewportRef.current;
    if (!sheet || !viewport || status !== "success") return undefined;

    const measure = () => {
      const previousTransform = sheet.style.transform;
      sheet.style.transform = "none";
      const width = sheet.offsetWidth;
      const height = sheet.offsetHeight;
      sheet.style.transform = previousTransform;
      if (!width || !height) return;
      setNaturalSize({ width, height });
      const availableWidth = viewport.clientWidth;
      setScale(availableWidth > 0 ? Math.min(1, availableWidth / width) : 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [status, receipt]);

  const pdfUrl = `${config.apiBaseUrl}/payment-receive/hpu/receipts/public/${encodeURIComponent(token || "")}/pdf`;

  const viewPdf = () => {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfError("");
    try {
      const response = await axios.get(pdfUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${receipt?.receipt_number || "TEBAHPU-receipt"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError("Could not download the receipt PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const scaledHeight = naturalSize.height ? naturalSize.height * scale : undefined;
  const scaledWidth = naturalSize.width ? naturalSize.width * scale : undefined;

  return (
    <ThemeProvider theme={hpcTheme}>
      <CssBaseline />
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .hpu-print-area, .hpu-print-area * { visibility: visible; }
          .hpu-print-area { position: absolute; top: 0; left: 0; margin: 0; }
          .hpu-sheet {
            position: static !important;
            transform: none !important;
            margin: 0 !important;
            box-shadow: none !important;
            width: ${A4_WIDTH_MM}mm !important;
          }
          .hpu-scale-box { height: auto !important; }
          .hpu-scroll { height: auto !important; overflow: visible !important; }
        }
        @page { size: A4; margin: 0; }

        .hpu-sheet {
          width: ${A4_WIDTH_MM}mm;
          box-sizing: border-box;
          background: #ffffff;
          color: #17202a;
          font-family: Arial, Helvetica, sans-serif;
          padding: 10mm 12mm;
          box-shadow: 0 8px 24px rgba(0,0,0,0.25);
        }
        .hpu-sheet-header { text-align: center; border-bottom: 2px solid #f5a623; padding-bottom: 3mm; margin-bottom: 4mm; }
        .hpu-sheet-header img { max-width: 32mm; max-height: 16mm; object-fit: contain; }
        .hpu-sheet-header h1 { margin: 1mm 0 0; font-size: 15px; letter-spacing: 1px; }
        .hpu-sheet-header p { margin: 0.5mm 0 0; font-size: 10px; color: #52606d; text-transform: uppercase; letter-spacing: 1.5px; }
        .hpu-meta { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 3mm; }
        .hpu-section { margin-bottom: 3.5mm; }
        .hpu-section-title { font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #b66e00; border-bottom: 1px solid #d9e2ec; padding-bottom: 1mm; margin-bottom: 1.5mm; }
        .hpu-row { display: flex; justify-content: space-between; gap: 6mm; font-size: 11px; padding: 0.8mm 0; }
        .hpu-row-label { color: #52606d; }
        .hpu-row-value { font-weight: 600; text-align: right; word-break: break-word; }
        .hpu-total { display: flex; justify-content: space-between; font-size: 13px; font-weight: bold; border-top: 1.5px solid #17202a; border-bottom: 1.5px solid #17202a; padding: 2mm 0; margin-top: 1mm; }
        .hpu-sheet-footer { text-align: center; margin-top: 5mm; font-size: 9.5px; color: #52606d; }
      `}</style>

      {/* The page body cannot scroll (app.css), so this page is its own scroll area, like HpcSuccess. */}
      <Box
        className="hpu-scroll"
        sx={{
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          background: `linear-gradient(180deg, ${HPC_COLORS.navy} 0%, ${HPC_COLORS.navyMid} 100%)`,
        }}
      >
      <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        {status === "loading" && (
          <Box sx={{ textAlign: "center", color: "#fff", pt: 8 }}>
            <CircularProgress size={28} sx={{ mb: 2 }} />
            <Typography variant="body1">Loading your receipt…</Typography>
          </Box>
        )}

        {status === "not_found" && (
          <Box sx={{ textAlign: "center", color: "#fff", pt: 8, px: 2 }}>
            <Typography variant="h6" gutterBottom>
              Receipt not found
            </Typography>
            <Typography variant="body2" sx={{ color: HPC_COLORS.silver }}>
              This receipt link is invalid or no longer available. Please contact TEBA HPU if you believe this is a mistake.
            </Typography>
          </Box>
        )}

        {status === "error" && (
          <Box sx={{ textAlign: "center", color: "#fff", pt: 8, px: 2 }}>
            <Alert severity="error" sx={{ mb: 2, textAlign: "left", display: "inline-flex" }}>
              Something went wrong while loading this receipt. Please check your connection and try again.
            </Alert>
            <Box>
              <Button startIcon={<ReplayIcon />} variant="outlined" color="inherit" onClick={loadReceipt}>
                Try again
              </Button>
            </Box>
          </Box>
        )}

        {status === "success" && receipt && (
          <>
            {/* Action bar — intentionally outside the printable receipt sheet */}
            <Box
              className="hpu-no-print"
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "center",
                mb: 2,
                flexWrap: "wrap",
                position: "sticky",
                top: 0,
                zIndex: 2,
                py: 1,
                backgroundColor: "rgba(10, 22, 40, 0.92)",
                borderRadius: 1,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
              >
                Print Receipt
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
                startIcon={<OpenInNewIcon />}
                onClick={viewPdf}
              >
                View PDF
              </Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadPdf} disabled={pdfBusy}>
                {pdfBusy ? "Preparing…" : "Download PDF"}
              </Button>
            </Box>
            {pdfError && (
              <Box className="hpu-no-print" sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Alert severity="error">{pdfError}</Alert>
              </Box>
            )}

            {/* Printable, A4-proportioned receipt sheet */}
            <Box ref={viewportRef} className="hpu-print-area" sx={{ width: "100%" }}>
              <Box
                className="hpu-scale-box"
                sx={{ position: "relative", height: scaledHeight, mx: "auto" }}
              >
                <div
                  ref={sheetRef}
                  className="hpu-sheet"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    marginLeft: scaledWidth ? -(scaledWidth / 2) : 0,
                  }}
                >
                  <div className="hpu-sheet-header">
                    <img src={HPC_BRAND.logoSrc} alt="TEBA HPU logo" />
                    <h1>TEBA HPU — PAYMENT RECEIPT</h1>
                    <p>Health &amp; Performance Unit</p>
                  </div>

                  <div className="hpu-meta">
                    <span>Receipt No: <strong>{displayValue(receipt.receipt_number)}</strong></span>
                    <span>Date: <strong>{displayValue(receipt.payment_date)}</strong></span>
                  </div>

                  <div className="hpu-section">
                    <div className="hpu-section-title">Member</div>
                    <Row label="Name" value={receipt.member?.full_name} />
                    <Row label="Membership ID" value={receipt.member?.membership_id} />
                  </div>

                  <div className="hpu-section">
                    <div className="hpu-section-title">Membership</div>
                    <Row label="Package" value={formatLabel(receipt.membership?.package)} />
                    <Row label="Start" value={receipt.membership?.activation_date} />
                    <Row label="Expiry" value={receipt.membership?.expiry_date} />
                  </div>

                  <div className="hpu-section">
                    <div className="hpu-section-title">Payment</div>
                    <Row label="Method" value={formatLabel(receipt.payment?.payment_method)} />
                    <Row
                      label="Description"
                      value={receipt.payment?.description || formatLabel(receipt.payment?.package_service)}
                    />
                    {receipt.promotion && (
                      <>
                        <Row label="Original Value" value={formatRs(receipt.promotion.original_value)} />
                        <Row
                          label="Promotion"
                          value={`${receipt.promotion.code} −${formatRs(receipt.promotion.discount_amount)}`}
                        />
                        <Row label="Promotion Price" value={formatRs(receipt.promotion.final_amount)} />
                      </>
                    )}
                    <Row label="Amount Paid" value={formatRs(receipt.payment?.amount)} />
                    <Row label="Previous Outstanding" value={formatRs(receipt.payment?.previous_outstanding_amount)} />
                    <Row label="Balance After Payment" value={formatRs(receipt.payment?.balance_after_payment)} />
                    <div className="hpu-total">
                      <span>TOTAL PAID</span>
                      <span>{formatRs(receipt.payment?.amount)}</span>
                    </div>
                  </div>

                  {receipt.family && (
                    <div className="hpu-section">
                      <div className="hpu-section-title">Family Members</div>
                      {(receipt.family.members || []).map((member) => (
                        <Row
                          key={`${member.position}-${member.full_name}`}
                          label={`#${displayValue(member.position)} ${member.full_name || ""}`}
                          value={`${member.price_category === "student" ? "Student" : "Adult"} · ${formatRs(member.net_fee)}`}
                        />
                      ))}
                      {receipt.family.expected_total != null && (
                        <Row label="Expected Family Total" value={formatRs(receipt.family.expected_total)} />
                      )}
                    </div>
                  )}

                  <div className="hpu-sheet-footer">
                    Thank you for your payment. This is a computer generated receipt.
                  </div>
                </div>
              </Box>
            </Box>
          </>
        )}
      </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PublicHpuReceipt;

